import { useNavigate } from "react-router-dom";
import { resolveInterviewDetailPath } from "../../../components/interviewList/detailPath";
import {
  resolveInterviewDisplayStatus,
  STAKEHOLDER_ANSWERS_IMPORTED,
} from "../../../components/interviewList/status";
import { useMyInterviewsGraphQL } from "../../../hooks/useMyInterviews";

const STATUS_LABELS: Record<string, string> = {
  SCHEDULED: "インタビュー実施前",
  INTERVIEWED: "インタビュー実施済み",
  COMPLETED: "完了",
  SUMMARY_CHECK_PENDING: "要約チェック待ち",
  SUMMARY_CHECK_COMPLETED: "要約チェック完了",
  CANCELLED: "キャンセル済み",
  [STAKEHOLDER_ANSWERS_IMPORTED]: "回答取り込み済み",
};

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  SCHEDULED: { bg: "#dbeafe", color: "#1e40af" },
  INTERVIEWED: { bg: "#dcfce7", color: "#166534" },
  COMPLETED: { bg: "#dcfce7", color: "#166534" },
  SUMMARY_CHECK_PENDING: { bg: "#fef3c7", color: "#92400e" },
  SUMMARY_CHECK_COMPLETED: { bg: "#f3f4f6", color: "#374151" },
  CANCELLED: { bg: "#fee2e2", color: "#991b1b" },
  [STAKEHOLDER_ANSWERS_IMPORTED]: { bg: "#dcfce7", color: "#166534" },
};

function StatusBadge({
  interview,
}: {
  interview: {
    status: string;
    zoomRecordingViewUrl: string | null;
    stakeholderContext: { answersImported: boolean } | null;
  };
}) {
  const status = resolveInterviewDisplayStatus(interview);
  return (
    <span
      className="whitespace-nowrap rounded-xl px-2.5 py-1 font-medium text-[11px]"
      style={{
        backgroundColor: STATUS_STYLES[status]?.bg || "#f3f4f6",
        color: STATUS_STYLES[status]?.color || "#374151",
      }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function InterviewerDashboard() {
  const navigate = useNavigate();
  const { interviews, loading } = useMyInterviewsGraphQL();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-subtle-gray)]">
        <div className="p-10 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-4">
          <nav className="flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
            <span className="font-medium text-[var(--color-text-primary)]">
              インタビュアーダッシュボード
            </span>
          </nav>
        </div>

        <div className="mb-8">
          <div className="border-[var(--color-primary-500)] border-l-4 pl-5">
            <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
              担当インタビュー一覧
            </h1>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <div className="border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
            <h2 className="m-0 font-bold text-[24px] text-[var(--color-primary-500)]">
              インタビュー一覧
            </h2>
          </div>

          <div className="p-6">
            {interviews.length === 0 ? (
              <div className="p-12 text-center text-[#9ca3af]">
                担当インタビューがありません
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      日時
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      会社
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      候補者
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      ターム
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      ステータス
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {interviews.map((interview) => (
                    <tr
                      key={interview.interviewId}
                      className="cursor-default hover:bg-[var(--color-bg-subtle-gray)]"
                    >
                      <td className="border-[var(--color-border-default)] border-b px-4 py-3 text-[14px] text-[var(--color-text-primary)]">
                        <div>
                          <div className="font-medium">
                            {interview.startTime
                              ? formatDate(interview.startTime)
                              : "-"}
                          </div>
                          {interview.startTime && interview.endTime && (
                            <div className="mt-0.5 text-[12px] text-[var(--color-text-secondary)]">
                              {formatTime(interview.startTime)}-
                              {formatTime(interview.endTime)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="border-[var(--color-border-default)] border-b px-4 py-3 font-medium text-[14px] text-[var(--color-text-primary)]">
                        {interview.sponsorName || "-"}
                      </td>
                      <td className="border-[var(--color-border-default)] border-b px-4 py-3 font-medium text-[14px] text-[var(--color-text-primary)]">
                        <div>{interview.candidateName || "-"}</div>
                        {interview.candidateNameKana && (
                          <div className="mt-0.5 font-normal text-[12px] text-[var(--color-text-secondary)]">
                            {interview.candidateNameKana}
                          </div>
                        )}
                      </td>
                      <td className="border-[var(--color-border-default)] border-b px-4 py-3 text-[14px] text-[var(--color-text-primary)]">
                        {interview.termNotionUrl ? (
                          <a
                            href={interview.termNotionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[var(--color-primary-500)] underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {interview.termTitle || "-"}
                          </a>
                        ) : (
                          interview.termTitle || "-"
                        )}
                      </td>
                      <td className="border-[var(--color-border-default)] border-b px-4 py-3">
                        <StatusBadge interview={interview} />
                      </td>
                      <td className="border-[var(--color-border-default)] border-b px-4 py-3">
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() =>
                              navigate(resolveInterviewDetailPath(interview))
                            }
                            className="h-7 cursor-pointer rounded-md border border-[var(--color-primary-500)] bg-transparent px-2 py-1 font-medium text-[11px] text-[var(--color-primary-500)] hover:bg-[var(--color-primary-50)]"
                          >
                            詳細
                          </button>
                          {/* ステークホルダーは AI 要約を作らないため、チェックする対象がない */}
                          {!interview.stakeholderContext &&
                            (interview.status === "INTERVIEWED" ||
                              interview.status === "SUMMARY_CHECK_PENDING" ||
                              interview.status ===
                                "SUMMARY_CHECK_COMPLETED") && (
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/employee_interview/interviewer/summary-check/${interview.interviewId}`,
                                  )
                                }
                                className="h-7 cursor-pointer rounded-md border-none bg-[var(--color-orange-500)] px-2 py-1 font-medium text-[11px] text-white hover:bg-[var(--color-orange-600)]"
                              >
                                要約チェック
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
